// Library Constants

import airtable_config_dev from './config/airtableDevelopment.js';
import airtable_config_prod from './config/airtableProduction.js';
import general_config_dev from './config/generalDevelopment.js';
import general_config_prod from './config/generalProduction.js';

import latestPerceptualSurvey from '../community-hats-js-library/assets/js_surveys/perceptualSurveyConfiguration.js';
import latestParticipantIntakeForm from '../community-hats-js-library/assets/js_surveys/participantIntakeSurveyConfiguration.js';
import latestHouseIntakeForm from '../community-hats-js-library/assets/js_surveys/houseIntakeSurveyConfiguration.js';
import latestPerceptualStreetVendorSurvey from '../community-hats-js-library/assets/js_surveys/perceptualStreetVendorSurveyConfiguration.js';
import latestParticipantStreetVendorIntakeForm from '../community-hats-js-library/assets/js_surveys/participantStreetVendorIntakeSurveyConfiguration.js';
import perceptualMentalHealthSurveyConfiguration from '../community-hats-js-library/assets/js_surveys/perceptualMentalHealthSurveyConfiguration.js';
import latestWomenHealthPerceptualSurvey from '../community-hats-js-library/assets/js_surveys/perceptualWomensHealthSurveyConfiguration.js';

// Development
export const DEVELOPMENT = `${process.env.REACT_APP_DEVELOPMENT}`.toUpperCase() === 'TRUE';

export const REACT = 'React';
export const NODE = 'Node';

export function detectEnvironment() {
  if (typeof window !== 'undefined' && typeof window.document !== 'undefined') {
    return REACT;
  } else if (typeof process !== 'undefined' && process.versions && process.versions.node) {
    return NODE;
  }
  return 'Unknown Environment';
}

export const AIRTABLE_CONFIG = DEVELOPMENT ? airtable_config_dev : airtable_config_prod;

// Values tied to the study and its location (see config/README.md).
// A missing or malformed key fails at import, the way the Python library does:
// a missing timezone would otherwise silently fall back to the host's.
// Both files are checked, so a key added to only one of them fails everywhere.
const REQUIRED_GENERAL_KEYS = ['timezone', 'languages', 'api_url', 'api_url_front'];

const validateGeneralConfig = (config, fileName) => {
  for (const key of REQUIRED_GENERAL_KEYS)
    if (config === null || typeof config !== 'object' || config[key] === undefined || config[key] === null)
      throw new Error(`config/${fileName} has no '${key}' key. See config/README.md`);

  try {
    new Intl.DateTimeFormat('en', { timeZone: config.timezone });
  } catch (e) {
    throw new Error(`config/${fileName} has an invalid timezone: '${config.timezone}'. See config/README.md`);
  }

  if (!Array.isArray(config.languages))
    throw new Error(`config/${fileName}: 'languages' must be an array. See config/README.md`);

  // Every use is `${API_URL}/...`, so a trailing slash would double up
  for (const key of ['api_url', 'api_url_front']) {
    let apiUrl = null;
    try {
      apiUrl = new URL(config[key]);
    } catch (e) {}
    if (apiUrl === null || !['https:', 'http:'].includes(apiUrl.protocol) || `${config[key]}`.endsWith('/'))
      throw new Error(
        `config/${fileName}: '${key}' must be an http(s) URL without a trailing slash, got '${config[key]}'. See config/README.md`
      );
  }
};

validateGeneralConfig(general_config_dev, 'generalDevelopment.js');
validateGeneralConfig(general_config_prod, 'generalProduction.js');

export const GENERAL_CONFIG = DEVELOPMENT ? general_config_dev : general_config_prod;

// The secrets are handed in by whoever is able to read them. In node that is
// the server, at start up, from config/config_{development,production}.json.
//
// Nothing here reads that file. It is gitignored, it is absent from a browser
// bundle entirely, and reaching for it would mean importing `fs` into a module
// the React client also imports. Injection is what keeps this file isomorphic.
let configValues = null;

/**
 * Hands the library its configuration. Call once, before anything that needs a
 * secret. The server does this from utils/config.js, as an import side effect,
 * so it happens before any consuming module's body runs.
 * @param {object} values the parsed config file
 */
export const setConfig = (values) => {
  configValues = values;
};

/**
 * Reads a configuration value.
 *
 * Synchronous. It used to await a fetch in the browser, and every call site
 * still says `await CONFIG(...)`, which is harmless and left alone.
 *
 * @param {string} path a key, or a dotted path for nested values such as
 *                      'google_health_api.community-hats.app_id'
 * @returns the value, or null if any segment of the path is missing
 */
export const CONFIG = (path) => {
  if (configValues === null)
    throw new Error(
      `CONFIG("${path}") was called before setConfig(). The server injects the configuration at start up; nothing running in a browser may call CONFIG.`
    );

  let current = configValues;

  for (const segment of `${path}`.split('.')) {
    // hasOwnProperty rather than `in`, so that a path like "constructor" misses
    // instead of walking the prototype chain.
    if (current === null || typeof current !== 'object' || !Object.prototype.hasOwnProperty.call(current, segment))
      return null;

    current = current[segment];
  }

  return current;
};

// API URL, the application server's host for this environment
export const API_URL = GENERAL_CONFIG.api_url_front;
export const API_URL_DIRECT = GENERAL_CONFIG.api_url;

// Sensor Inbox
export const SENSOR_INBOX_EMAIL = 'sensor.inbox@community-hats.net';

// Application Versions
// Live
export const APPLICATION_VERSIONS_URL = `https://community-hats-public.s3.ap-south-1.amazonaws.com/application_versions.json`;
export const LOCATION_TRACKER_APP = `location-tracker-bt-app`;

// Long Jobs
//Wearable Coverage
export const WEARABLE_COVERAGE_URL = `${API_URL_DIRECT}/coverage/wearable`;

// Decryption
export const DECRYPT_PDF_URL = `${API_URL_DIRECT}/pdfs/decrypt`;

// Latest perceptual survey
export const LATEST_PERCEPTUAL_SURVEY = latestPerceptualSurvey;

// Latest womens health survey
export const LATEST_PERCEPTUAL_WOMENS_HEALTH_SURVEY = latestWomenHealthPerceptualSurvey;

// Latest Intake Form
export const LATEST_PARTICIPANT_INTAKE_FORM = latestParticipantIntakeForm;
export const LATEST_HOUSE_INTAKE_FORM = latestHouseIntakeForm;
export const LATEST_PARTICIPANT_STREET_VENDOR_INTAKE_FORM = latestParticipantStreetVendorIntakeForm;

// Latest Street Vendor Perceptual Survey
export const LATEST_PERCEPTUAL_STREET_VENDOR_SURVEY = latestPerceptualStreetVendorSurvey;

// Latest Mental Health Survey
export const LATEST_PERCEPTUAL_MENTAL_HEALTH_SURVEY = perceptualMentalHealthSurveyConfiguration;

// Map Constants
export const MAP_KEY = 'map_key';

// AirTable Constants
export const AT_API_KEY = 'airtable_key';
export const AT_APP_ID = 'at_app_id';

// AWS Constants
export const AWS_REGION = 'aws_region';
export const AWS_BUCKET = 'aws_sensors_bucket';
export const AWS_EXTRAS_BUCKET = 'aws_extras_bucket';
export const AWS_KEY = 'aws_access_key_js_library';
export const AWS_SECRET = 'aws_secret_key_js_library';
export const AWS_SHARE_BUCKET = 'aws_share_bucket';

// Reports
export const PARTICIPANTS_REPORT_FOLDER = 'participant_reports';

// Dashboard folder
export const DASHBOARD_FOLDER = 'dashboards';

// Data Streams
export const DATA_STREAMS_JSON = 'data_streams_summary.json';

// Fitbit API
export const FITBIT_APP_ID = '23S2FK';
export const FITBIT_VERIFICATION_CODE = 'fitbit_verify_code';
export const FITBIT_CLIENT_SERVER_SECRET = 'fitbit_server_client_secret';
// The separator of the retired copy and paste link flow. Kept because old
// clipboard payloads still carry it; nothing builds one any more.
export const FITBIT_API_SEP = '---';

// Fitbit OAuth. Shaped exactly like the Google Health block below, because the
// two flows are the same flow: the manager asks for a consent URL, the field
// manager consents, and the callback on this server is the only place a token
// ever exists.
export const FITBIT_OAUTH_AUTH_URL = 'https://www.fitbit.com/oauth2/authorize';
export const FITBIT_OAUTH_TOKEN_URL = 'https://api.fitbit.com/oauth2/token';
export const FITBIT_REDIRECT_URI = `${API_URL_DIRECT}/fitbit/callback`;

// Must match, exactly, the scopes registered for FITBIT_APP_ID.
export const FITBIT_SCOPES = ['activity', 'heartrate', 'sleep', 'temperature', 'settings', 'profile'];

// A year, which is what the app is registered for. Unrelated to the access
// token's own lifetime; this is how long the grant itself lasts.
export const FITBIT_OAUTH_EXPIRES_IN_SECONDS = 31536000;

// Google Health API
// Replaces the Fitbit Web API, which sunsets in September 2026.
export const GOOGLE_OAUTH_AUTH_URL = 'https://accounts.google.com/o/oauth2/v2/auth';
export const GOOGLE_OAUTH_TOKEN_URL = 'https://oauth2.googleapis.com/token';
export const GOOGLE_USERINFO_URL = 'https://www.googleapis.com/oauth2/v3/userinfo';
// Answers what a token is actually good for. Used to check a grant rather than
// to read data, so that a missing scope is reported as a missing scope instead
// of surfacing later as an opaque 403 from a data call.
export const GOOGLE_TOKENINFO_URL = 'https://oauth2.googleapis.com/tokeninfo';
export const GOOGLE_REDIRECT_URI = `${API_URL_DIRECT}/google/callback`;
// Where an account with no GCP project assigned is sent instead of the consent
// screen. Served by this project's server, not by Google.
export const GOOGLE_NO_PROJECT_URL = `${API_URL_DIRECT}/google/no-project`;

// Configuration keys, built here for the same reason FITBIT_CLIENT_SERVER_SECRET
// is named here: the string is what the config file uses, and only one file
// should know it.
//
// These are builders rather than constants because there is no longer a single
// Google project. An unverified GCP project is capped at 100 users, so the study
// is spread across several, and which one an account belongs to is read from the
// GCP Project column on its Emails record (AT_GCP_PROJECT). A constant that
// silently meant one particular project is exactly what made the code exchange
// and the token refresh reach for the wrong OAuth client.
export const googleHealthAppIdKey = (project) => `google_health_api.${project}.app_id`;
export const googleHealthClientSecretKey = (project) => `google_health_api.${project}.server_client_secret`;
export const SERVER_STATE_SECRET = 'server_state_secret';

// Every googlehealth.* scope is Restricted and must be registered on the
// project's Data Access page before it can be requested. userinfo.email is what
// lets the callback confirm who actually consented.
export const GOOGLE_HEALTH_SCOPES = [
  'https://www.googleapis.com/auth/googlehealth.activity_and_fitness.readonly',
  'https://www.googleapis.com/auth/googlehealth.health_metrics_and_measurements.readonly',
  'https://www.googleapis.com/auth/googlehealth.sleep.readonly',
  'https://www.googleapis.com/auth/googlehealth.settings.readonly',
  'https://www.googleapis.com/auth/userinfo.email',
];

// How long a consent link stays usable. Lives here rather than on the server so
// that the code signing the state and the code verifying it cannot drift apart.
// Google in the name, but the signed state carries only { email, nonce, exp }
// and the Fitbit flow uses the very same machinery, so this bounds both.
export const GOOGLE_OAUTH_STATE_TTL_SECONDS = 600;

// ---------------------------------------------------------------------------
// Google Health API, the data plane. Everything above is OAuth; everything here
// reads data with the token that flow produces.
//
// These mirror the constants at the top of the Python library's
// utils/google_health_functions.py, which made this migration first. Where the
// two disagree the Python file is the one that has run in production, so the
// names are kept close enough to grep across both.
// ---------------------------------------------------------------------------
export const GOOGLE_HEALTH_API_URL = 'https://health.googleapis.com/v4';

// Google addresses the grant holder as "me". There is no Google equivalent of
// Fitbit's requestId, which is why nothing on this side takes one.
export const GOOGLE_HEALTH_USER_PATH = 'users/me';

// The data type as it appears in a URL. Note the API spells the very same type
// with a hyphen in the path and an underscore in a filter expression, which is
// why the filter field below is a separate constant rather than derived: one
// constant would invite using the wrong spelling in one of the two places.
export const GOOGLE_HEALTH_DATA_TYPE_HEART_RATE = 'heart-rate';

// The aggregate to read out of a rolled up heart rate window. Averages come back
// as numbers and sums as strings, so callers coerce. Note it is beatsPerMinuteAvg
// and not beatsPerMinuteAverage, which is how the reference summary renders it.
export const GOOGLE_HEALTH_HEART_RATE_VALUE_KEY = 'beatsPerMinuteAvg';

export const googleHealthDataPointsURL = (dataType) =>
  `${GOOGLE_HEALTH_API_URL}/${GOOGLE_HEALTH_USER_PATH}/dataTypes/${dataType}/dataPoints`;

// Aggregation happens server side. Listing raw samples instead would pull on the
// order of 36.000 heart rate points for a single day against the 288 five minute
// windows this returns, which is the reason the Python library reads it this way.
export const googleHealthRollUpURL = (dataType) => `${googleHealthDataPointsURL(dataType)}:rollUp`;

export const GOOGLE_HEALTH_PAIRED_DEVICES_URL = `${GOOGLE_HEALTH_API_URL}/${GOOGLE_HEALTH_USER_PATH}/pairedDevices`;

// A paired device is a TRACKER or a SCALE, and a scale says nothing about whether
// the participant is wearing anything. The analogue of the Fitbit filter that
// drops deviceVersion === 'MobileTrack', not a translation of it: the Google list
// has no phone pseudo device in it to begin with.
export const GOOGLE_HEALTH_TRACKER_DEVICE_TYPE = 'TRACKER';

// The window a rolled up heart rate is aggregated into, in seconds. The server
// side equivalent of the 5min periodicity the Fitbit intraday calls ask for.
export const GOOGLE_HEALTH_HEART_RATE_WINDOW_SECONDS = 300;

// Fitbit reported the latest reading by the minute, so the one call that looks
// for it rolls up by the minute too.
export const GOOGLE_HEALTH_LAST_HEART_RATE_WINDOW_SECONDS = 60;

// The largest range the roll up endpoint accepts in one request. It rejects a
// call whose windowSize * pageSize covers more than the data type's maximum
// range, so the page size is derived from the window rather than maximized.
export const GOOGLE_HEALTH_MAX_QUERY_DAYS = 14;

// How long to wait out a 429 before the single retry, and how long to pause
// between pages. Both taken from the Python library, which set them against the
// real quota rather than against the documentation.
export const GOOGLE_HEALTH_RETRY_SLEEP_SECONDS = 180;
export const GOOGLE_HEALTH_SLEEP_SECONDS = 5;

// The bin width the wearable coverage columns are defined in. Named here rather
// than left to a default argument because the Fitbit and the Google coverage
// numbers only mean the same thing if both are computed against it.
export const WEARABLE_COVERAGE_MINUTE_FREQUENCY = 5;

// Defaults
export const DEFAULT_PHONE_NUMBER = '0000000000';

// Accuracy
export const IDEAL_ACCURACY = 15;

// Intake Form
export const INTAKE_FORM_LINK =
  'https://docs.google.com/forms/d/e/1FAIpQLSflKny18QvRLvcE-kGeb3KHR3hqUk6y8Ng0GHGe16crnMEXcA/viewform';
export const INTAKE_FORM_FIELD_ID_NAME = 'entry.63757476';
export const INTAKE_FORM_FIELD_ID_LAST_NAME = 'entry.658433398';
export const INTAKE_FORM_FIELD_ID_SEWA_ID = 'entry.761365968';

// Capacities
export const INPIRE_3_CAPACITY = 30;
export const GOVEE_CAPACITY = 20;
export const KESTREL_CAPACITY = 20;
export const HOBO_CAPACITY = 180;

// Status
export const STATUS_DATASET_PREFIX = 'STATUS_DATASET_';
export const STATUS_DATASET_PARTICIPANTS = `${STATUS_DATASET_PREFIX}PARTICIPANTS`;
export const STATUS_DATASET_SENSORS = `${STATUS_DATASET_PREFIX}SENSORS`;
export const STATUS_DATASET_HOUSES = `${STATUS_DATASET_PREFIX}HOUSES`;
export const STATUS_DATASET_EMAILS = `${STATUS_DATASET_PREFIX}EMAILS`;
export const STATUS_DATASET_PARTICIPANT_PLACEMENT = `${STATUS_DATASET_PREFIX}PARTICIPANT_PLACEMENT`;
export const STATUS_DATASET_SENSOR_HOUSE_PLACEMENT = `${STATUS_DATASET_PREFIX}SENSOR_HOUSE_PLACEMENT`;
export const STATUS_DATASET_WEARABLES = `${STATUS_DATASET_PREFIX}WEARABLES`;
export const STATUS_DATASET_PHONES = `${STATUS_DATASET_PREFIX}PHONES`;
export const STATUS_DATASET_WEARABLE_PLACEMENT = `${STATUS_DATASET_PREFIX}WEARABLE_PLACEMENT`;
export const STATUS_DATASET_PHONE_PLACEMENT = `${STATUS_DATASET_PREFIX}PHONE_PLACEMENT`;
export const STATUS_DATASET_RECEIVED_SENSOR_FILES = `${STATUS_DATASET_PREFIX}RECEIVED_SENSOR_FILES`;
export const STATUS_DATASET_ISSUES = `${STATUS_DATASET_PREFIX}ISSUES`;
export const STATUS_DATASET_GOVEE_ACTIVATION_CODES = `${STATUS_DATASET_PREFIX}GOVEE_ACTIVATION_CODES`;
export const STATUS_DATASET_COLLECTION_RECORDS = `${STATUS_DATASET_PREFIX}COLLECTION_RECORDS`;
export const STATUS_DATASET_COVERAGE_SUMMARY = `${STATUS_DATASET_PREFIX}COVERAGE_SUMMARY`;
export const STATUS_DATASET_BATTERY_CHANGE_RECORDS = `${STATUS_DATASET_PREFIX}BATTERY_CHANGE_RECORDS`;

// Datasets
export const DATASET_PREFIX = 'DATASET_';
export const DATASET_PARTICIPANTS = `${DATASET_PREFIX}PARTICIPANTS`;
export const DATASET_SENSORS = `${DATASET_PREFIX}SENSORS`;
export const DATASET_HOUSES = `${DATASET_PREFIX}HOUSES`;
export const DATASET_EMAILS = `${DATASET_PREFIX}EMAILS`;
export const DATASET_WEARABLES = `${DATASET_PREFIX}WEARABLES`;
export const DATASET_PHONES = `${DATASET_PREFIX}PHONES`;
export const DATASET_PARTICIPANT_PLACEMENT = `${DATASET_PREFIX}PARTICIPANT_PLACEMENT`;
export const DATASET_WEARABLE_PLACEMENT = `${DATASET_PREFIX}WEARABLE_PLACEMENT`;
export const DATASET_PHONE_PLACEMENT = `${DATASET_PREFIX}PHONE_PLACEMENT`;
export const DATASET_SENSOR_HOUSE_PLACEMENT = `${DATASET_PREFIX}SENSOR_HOUSE_PLACEMENT`;
export const DATASET_RECEIVED_SENSOR_FILES = `${DATASET_PREFIX}RECEIVED_SENSOR_FILES`;
export const DATASET_ISSUES = `${DATASET_PREFIX}ISSUES`;
export const DATASET_GOVEE_ACTIVATION_CODES = `${DATASET_PREFIX}GOVEE_ACTIVATION_CODES`;
export const DATASET_COLLECTION_RECORDS = `${DATASET_PREFIX}COLLECTION_RECORDS`;
export const DATASET_COVERAGE_SUMMARY = `${DATASET_PREFIX}COVERAGE_SUMMARY`;
export const DATASET_BATTERY_CHANGE_RECORDS = `${DATASET_PREFIX}BATTERY_CHANGE_RECORDS`;

// General Contants
export const ID = 'id';
export const VALUE = 'value';
export const ERROR = 'error';
export const LOADING = 'loading';
export const OK = 'ok';
export const KEY = 'key';
export const EXTRA_KEY = 'extra_key';
export const DATE = 'date';
export const YES = 'Yes';
export const NO = 'No';
export const NA = 'NA';
export const MISSING = 'MISSING';
export const START_DATE = 'start_date';
export const END_DATE = 'end_date';
export const DURATION = 'duration';
export const COVERAGE = 'coverage';
export const INFO = 'info';
export const TEXT = 'text';
export const LINK = 'link';
export const CONTAINER = 'container';
export const STATUS = 'status';
export const FIELDS = 'fields';
export const POSITION_ID = 'position_id';
export const OBJECT = 'object';
export const LOADING_INFO = 'loading_info';
export const DONE = 'done';
export const MESSAGE = 'message';
export const TABLE_CONFIG_KEY = 'table_config_key';
export const NECESSARY_IDS = 'necessary_ids';
export const OPTIONAL_IDS = 'optional_ids';
export const COMPUTE_ON_UPLOAD = 'compute_on_upload';
export const DERIVATE_IDS = 'derivate_ids';
export const SINGLE_DEPENDENCIES = 'single_dependencies';
export const SET_DEPENDENCIES = 'array_dependencies';
export const IS_INSIDE_SET = 'is_inside_array';
export const SEARCH = 'search';
export const CODE = 'code';
export const HEART_RATE = 'heart_rate';
export const TEMPERATURE = 'temperature';
export const HUMIDITY = 'humidity';
export const DATETIME = 'datetime';
export const HEART_RATE_COVERAGE = 'heart_rate_coverage';
export const MORNING = 'morning';
export const AFTERNOON = 'afternoon';
export const EVENING = 'evening';
export const NIGHT = 'night';
export const ALL = 'all';

// Status
export const STATUS_OK = 'STATUS_OK';
export const STATUS_LOADING = 'STATUS_LOADING';
export const STATUS_NOT_STARTED = 'STATUS_NOT_STARTED';
export const STATUS_NOT_FOUND = 'STATUS_NOT_FOUND';
export const STATUS_ERROR = 'STATUS_ERROR';

// Sensor Brands
export const KESTREL = 'Kestrel';
export const GOVEE = 'Govee';
export const HOBO = 'Hobo';
// Models
export const U12_HOBO_MODEL = 'U12-013';
export const MX_HOBO_MODEL = 'MX1104';

// Tools
export const DASHBOARD = 'Dashboard';
export const CH_MANAGERE_APP = 'CH Manager';

// Languages
export const EN = 'en';
export const GU = 'gu';
export const ES = 'es';
export const HI = 'hi';

export const ATF_DATE = 'Date';
export const ATF_ADDRESSVATION_CODE = 'Activation Code';
export const ATF_DATE_CREATED = 'Date Created';
export const ATF_SERIAL = 'Serial';
export const ATF_PUBLIC_ID = 'ID';
export const ATF_ADDRESS = 'Address';
export const ATF_AREA = 'Area';
export const ATF_SENSOR = 'Sensor';
export const ATF_PHONE = 'Phone';
export const ATF_WEARABLE = 'Wearable';
export const ATF_LOCATION = 'Location';
export const ATF_LATITUDE = 'Latitude';
export const ATF_LONGITUDE = 'Longitude';
export const ATF_ACCURACY = 'Accuracy';
export const ATF_LAST_UPLOADED = 'Last Uploaded';
export const ATF_TRACKER_SOFTWARE_VERSION = 'Tracker Software Version';
export const ATF_LAST_UPLOADED_FROM_FILES = 'Last Uploaded From Files';
export const ATF_LAST_MIGRATION = 'Last Migration';
export const ATF_LATEST_RECORD = 'Latest Record';
export const ATF_LATEST_RECORD_FROM_FILES = 'Lastest Record From Files';
export const ATF_BRAND = 'Brand';
export const ATF_MODEL = 'Model';
export const ATF_UPLOADED_DATE = 'Date Uploaded';
export const ATF_START_DATE = 'Start Date';
export const ATF_END_DATE = 'End Date';
export const ATF_TAG_COLOR = 'Tag Color';
export const ATF_TAG_CODE = 'Tag Code';
export const ATF_SEWA_ID = 'SEWA Id';
export const ATF_TYPE = 'Type';
export const ATF_SENSOR_PLACEMENTS = 'Sensor Placements';
export const ATF_SENSOR_PLACEMENT = 'Sensor Placement';
export const ATF_PHONE_PLACEMENTS = 'Phone Placement';
export const ATF_WEARABLE_PLACEMENT = 'Wearable Placement';
export const ATF_HAS_ACTIVE_SENSORS = 'Has Active Sensors';
export const ATF_ACTIVE_SENSORS = 'Active Sensors';
export const ATF_TOTAL_ACTIVE_SENSORS = 'Total Active Sensors';
export const ATF_HAS_ACTIVE_WEARABLES = 'Has Active Wearables';
export const ATF_ACTIVE_WEARABLE = 'Active Wearable';
export const ATF_HAS_ACTIVE_EMAILS = 'Has Active Account Email';
export const ATF_ACTIVE_EMAIL = 'Active Account Email';
export const ATF_HAS_ACTIVE_PHONES = 'Has Active Phones';
export const ATF_ACTIVE_PHONE = 'Active Phone';
export const ATF_RECEIVED_SENSOR_FILES = 'Received Sensor Files';
export const ATF_NAME = 'Name';
export const ATF_LAST_NAME = 'Last Name';
export const ATF_PHONE_NUMBER = 'Phone Number';
export const ATF_NOTES = 'Notes';
export const ATF_DAYS_UNTIL_EXPIRE = 'Days Until Expire';
export const ATF_DAYS_SINCE_LAST_HOUSE_VISIT = 'Days Since Last House Visit';
export const ATF_LATEST_COLLECTION_DATE = 'Latest Collection Date';
export const ATF_DATA_START = 'Data Start';
export const ATF_ACTIVE = 'Active';
export const ATF_MISSING = 'Missing';
export const ATF_JOINED_DATE = 'Joined Date';
export const ATF_DATE_UPLOADED = 'Date Uploaded';
export const ATF_SIZE_IN_DAYS = 'Size in Days';
export const ATF_FITBIT_TOKEN = 'Fitbit Token';
export const ATF_FITBIT_REFRESH_TOKEN = 'Refresh Token';
export const ATF_FITBIT_REQUEST_ID = 'Fitbit Request ID';
export const ATF_EMAIL = 'Email';
export const ATF_ACCOUNT_EMAIL = 'Account Email';
export const ATF_DEVICE_COLLECTION_STATUS = 'Device Collection Status';
export const ATF_DEVICE_COLLECTION_MESSAGE = 'Device Collection Message';
export const ATF_LATEST_BATTERY_CHANGE = 'Latest Battery Change';
export const ATF_BATTERY_CHANGE_RECORDS = 'Battery Change Records';
export const ATF_STATUS = 'Status';
export const ATF_PENDING = 'Pending';
export const ATF_DONE = 'Done';
export const ATF_SEVERITY = 'Severity';
export const ATF_HIGH = 'High';
export const ATF_MEDIUM = 'Medium';
export const ATF_LOW = 'Low';
export const ATF_READY_FOR_REVISION = 'Ready for Revision';
export const ATF_COLLECTION_RECORDS = 'Collection Records';
export const ATF_ISSUES = 'Issues';
export const ATF_RESPONSIBLE = 'Responsible';
export const ATF_GROUND_TEAM_ISSUES = 'Ground Team Issues';
export const ATF_PENDING_GROUND_TEAM_ISSUES = 'Pending Ground Team Issues';
export const ATF_PENDING_HIGH_PRIORITY_ISSUES = 'Pending High Priority Issues';
export const ATF_PARTICIPATION_COMPLETE_ISSUES = 'Participation Complete Issues';
export const ATF_GROUND_TEAM = 'Ground Team';
export const ATF_NUMERIC_ID = 'Numeric ID';
export const ATF_TAGS = 'Tags';
export const ATF_DESCRIPTION = 'Description';
export const ATF_DESCRIPTION_GU = 'Description Gu';
export const ATF_DESCRIPTION_ES = 'Description Es';
export const ATF_DESCRIPTION_HI = 'Description Hi';
export const ATF_ACTION = 'Action';
export const ATF_ACTION_GU = 'Action Gu';
export const ATF_ACTION_ES = 'Action Es';
export const ATF_ACTION_HI = 'Action Hi';
export const ATF_DATE_SOLVED = 'Date Solved';
export const ATF_PERCEPTUAL_STATUS = 'Perceptual Status';
export const ATF_REQUIRED = 'Required';
export const ATF_COMPLETE = 'Complete';
export const ATF_PENDING_QUESTIONS = 'Pending Questions';
export const ATF_INTAKE_SURVEY = 'Intake Form';
export const ATF_PERCEPTUAL_SURVEY = 'Perceptual Survey';
export const ATF_LAST_UPLOADED_PERCEPTUAL_SURVEY = 'Last Uploaded (Perceptual Survey)';
export const ATF_LAST_UPLOADED_WEARABLE = 'Last Uploaded (Wearable)';
export const ATF_PASSWORD = 'Password';
export const ATF_DROPBOX_PASSWORD = 'Dropbox Password';
export const ATF_GOVEE_PASSWORD = 'Govee Password';
export const ATF_ID_NAME = 'id';
export const ATF_PHONE_SERIAL = 'Phone Serial';
export const ATF_PLACEMENT_START = 'Placement Start Date';
export const ATF_FITBIT_CLIENT_ID = 'Fitbit Client ID';
export const ATF_FITBIT_TOKEN_EXPIRY_DATE = 'Fitbit Token Expiry Date';
export const ATF_PARTICIPANT = 'Participant';
export const ATF_HOUSE = 'House';
export const ATF_SENSORS = 'Sensors';
export const ATF_PLACEMENT_PHOTO = 'Placement Photo';
export const ATF_POSITION = 'Position';
export const ATF_OLD_POSITION = 'Old Position';
export const ATF_LOCATION_NUMERIC_ID = 'Location Numeric ID';
export const ATF_SENSOR_SERIAL = 'Sensor Serial';
export const ATF_ACTIVE_HOUSE = 'Active House';
export const ATF_ACTIVE_ACCOUNT_EMAIL = 'Active Account Email';
export const ATF_PARTICIPANT_PLACEMENT = 'Participant Placement';
export const ATF_DEVELOPMENT = 'Development';
export const ATF_INCOMPLETE = 'Incomplete';
export const ATF_DURATION = 'Duration';
export const ATF_DATE_OF_COLLECTION = 'Date of Collection';
export const ATF_TRADE = 'Trade';
export const ATF_HOMEBASE_WORKER = 'Homebase Worker';
export const ATF_AGRICULTURE = 'Agriculture';
export const ATF_BANGLE_FACTORY = 'Bangle Factory';
export const ATF_STREET_VENDOR = 'Street Vendor';
export const ATF_AHMEDABAD = 'Ahmedabad';
export const ATF_ANAND = 'Anand';
export const ATF_FIROZABAD = 'Firozabad';
export const ATF_ACTIVATION_CODE = 'Activation Code';
export const ATF_INTAKE_FORM_STATUS = 'Intake Form Status';
export const ATF_INTAKE_FORM_DATE = 'Intake Form Date';
export const ATF_PARTICIPANT_INTAKE_FORM = 'Participant Intake Form';
export const ATF_HOUSE_INTAKE_FORM = 'House Intake Form';
export const ATF_ALL_INTAKE_FORMS = 'All Intake Forms';
export const ATF_LATEST_HEART_RATE_UPLOAD = 'Latest Heart Rate Upload';
export const ATF_ACTIVE_PLACEMENT = 'Active Placement';
export const ATF_CASE_ID = 'Case ID';
export const ATF_LAB = 'Lab';
export const ATF_AGE = 'Age';
export const ATF_NAME_SCORE = 'Name Score';
export const ATF_SEWA_ID_SCORE = 'SEWA Id Score';
export const ATF_SCORE = 'Score';
export const ATF_SCORE_SECOND_BEST = 'Next Score';
export const ATF_NAME_SCORE_SECOND_BEST = 'Next Name Score';
export const ATF_NAME_SECOND_BEST = 'Next Name';
export const ATF_PHONE_SCORE_SECOND_BEST = 'Next Phone Score';
export const ATF_AGE_DIFF_SECOND_BEST = 'Next Age Diff';
export const ATF_TARGET_COMPLETE_NAME = 'Target Complete Name';
export const ATF_TARGET_SEWA_ID = 'Target SEWA Id';
export const ATF_TARGET_PHONE_NUMBER = 'Target Phone Number';
export const ATF_PHONE_SCORE = 'Phone Score';
export const ATF_TARGET_AGE = 'Target Age';
export const ATF_AGE_DIFF = 'Age Diff';
export const ATF_SAMPLE_DATE = 'Sample Date';
export const ATF_PENDING_QUESTIONS_IF = 'Pending Questions IF';
export const ATF_QUESTION_ID = 'Question ID';
export const ATF_ALL = 'All';
export const ATF_EMAIL_ADDRESS = 'Email Address';
export const ATF_LATEST_WEARABLE_COVERAGE_RECORD = 'Latest Wearable Coverage Record';
export const ATF_FILE_NAME = 'File Name';
export const ATF_SENSOR_POSITION_CHECKED_STATUS = 'Sensor Position Checked Status';
export const ATF_SENSOR_POSITION_CHECKED_DATE = 'Sensor Position Checked Date';
export const ATF_AVERAGE_COLLECTION_DURATION = 'Average Collection Duration';
export const ATF_WEARABLE_MORNING_COVERAGE = 'Wearable Morning Coverage';
export const ATF_WEARABLE_AFTERNOON_COVERAGE = 'Wearable Afternoon Coverage';
export const ATF_WEARABLE_EVENING_COVERAGE = 'Wearable Evening Coverage';
export const ATF_WEARABLE_NIGHT_COVERAGE = 'Wearable Night Coverage';
export const ATF_WEARABLE_TOTAL_COVERAGE = 'Wearable Total Coverage';
export const ATF_CYCLE = 'Cycle / Menstruating';
export const ATF_PREGNANT = 'Pregnant';
export const ATF_LACTATING = 'Lactating';
export const ATF_FEWER_CYCLES = 'Fewer Cycles';
export const ATF_NO_CYCLES = 'No Cycles';
export const ATF_FREQUENCY = 'Frequency';
export const ATF_START_MINUTES = 'Start Minutes';
export const ATF_FITBIT_TOKEN_STATUS = 'Fitbit Token Status';
export const ATF_INVALID = 'Invalid';
export const ATF_EXPIRED = 'Expired';
export const ATF_UNAUTHORIZED = 'Unauthorized';
export const ATF_ACTIVE_LOCATION = 'Active Location';
export const ATF_ALL_LOCATIONS = 'All Locations';
export const ATF_OTHER = 'Other';
export const ATF_STATUS_LAST_UPDTAED = 'Status Last Updated';
export const ATF_ISSUES_STATUS = 'Issues Status';
export const ATF_OVERLOOKED = 'Overlooked';
export const ATF_HOUSE_NUMERIC_ID = 'House Numeric ID';
export const ATF_SENSOR_PLACEMENT_ID = 'Sensor Placement ID';
export const ATF_PHOTO_RETAKE_STATUS = 'Photo Retake Status';
export const ATF_LATEST_PLACEMENT_START_DATE = 'Latest Placement Start Date';
export const ATF_VENDOR_SPOT = 'Vendor Spot';
export const ATF_FORM_TYPE = 'Form Type';
export const ATF_SURVEY_TYPE = 'Survey Type';
export const ATF_CURRENT_POSITION = 'Current Position';
export const ATF_BLUETOOTH_MAC = 'Bluetooth MAC';
export const ATF_ISSUE_SHARE_ID = 'Issue Share ID';
export const ATF_GOOGLE_TOKEN = 'Google Token';
export const ATF_GOOGLE_REFRESH_TOKEN = 'Google Refresh Token';
export const ATF_GOOGLE_TOKEN_EXPIRY_DATE = 'Google Token Expiry Date';
export const ATF_GOOGLE_TOKEN_STATUS = 'Google Token Status';

// Which Google Cloud project holds the OAuth client this account consented
// through. Read on every step of the Google flow, because a refresh token is
// only good against the client that issued it.
export const AT_GCP_PROJECT = 'GCP Project';
// The column's default. An account still carrying it has no OAuth client to send
// to Google, so the flow cannot start for it.
export const GCP_PROJECT_NOT_CONFIGURED = 'Not Configured';

export const TRADES = [ATF_HOMEBASE_WORKER, ATF_AGRICULTURE, ATF_BANGLE_FACTORY, ATF_STREET_VENDOR];
export const AREAS = [ATF_AHMEDABAD, ATF_ANAND, ATF_FIROZABAD];

export const ATF_WEARABLE_COVERAGE_COLUMNS = {
  0: 'Coverage 0',
  1: 'Coverage 1',
  2: 'Coverage 2',
  3: 'Coverage 3',
  4: 'Coverage 4',
  5: 'Coverage 5',
  6: 'Coverage 6',
  7: 'Coverage 7',
  8: 'Coverage 8',
  9: 'Coverage 9',
  10: 'Coverage 10',
  11: 'Coverage 11',
  12: 'Coverage 12',
  13: 'Coverage 13',
  14: 'Coverage 14',
  15: 'Coverage 15',
  16: 'Coverage 16',
  17: 'Coverage 17',
  18: 'Coverage 18',
  19: 'Coverage 19',
  20: 'Coverage 20',
  21: 'Coverage 21',
  22: 'Coverage 22',
  23: 'Coverage 23',
};

// Status and Messages
export const AT_SENSORS_COLLECTION_STATUS = 'Sensors Collection Status';
export const AT_SENSORS_COLLECTION_STATUS_LIST = 'Sensors Collection Status (List)';
export const AT_SENSORS_COLLECTION_MESSAGE = 'Sensors Collection Message';
export const AT_SENSORS_COLLECTION_MESSAGE_LIST = 'Sensors Collection Message (List)';
export const AT_COLLECTION_STATUS = 'Collection Status';
export const AT_COLLECTION_MESSAGE = 'Collection Message';

// Airtable Tables
export const AT_TABLE_GOVEE_ACTIVATION_CODES_ID = 'at_govee_activation_codes_id';
export const AT_TABLE_EMAILS = 'at_emails';
export const AT_TABLE_PARTICIPANTS = 'at_participants';
export const AT_TABLE_PHONES = 'at_phones';
export const AT_TABLE_PHONE_PLACEMENTS = 'at_phone_placements';
export const AT_TABLE_WEARABLE_PLACEMENTS = 'at_wearable_placements';
export const AT_TABLE_WEARABLES = 'at_wearables';
export const AT_TABLE_HOUSES = 'at_houses';
export const AT_TABLE_PARTICIPANTS_PLACEMENT = 'at_participant_placement';
export const AT_TABLE_SENSORS = 'at_sensors';
export const AT_TABLE_SENSOR_HOUSE_PLACEMENT = 'at_sensor_house_placement';
export const AT_TABLE_SENSOR_PLACEMENT_FORM = 'at_sensor_placement_form';
export const AT_TABLE_SENSOR_PHOTO_FORM = 'at_sensor_photo_form';
export const AT_TABLE_RECEIVED_SENSOR_FILES = 'at_received_sensor_files';
export const AT_TABLE_ISSUES = 'at_issues';
export const AT_TABLE_BUNDLES = 'at_bundles';
export const AT_TABLE_COLLECTION_RECORDS = 'at_collection_records';
export const AT_TABLE_PENDING_QUESTIONS_PARTICIPANT_IF = 'at_pending_questions_participant_if';
export const AT_TABLE_PENDING_QUESTIONS_HOUSE_IF = 'at_pending_questions_house_if';
export const AT_TABLE_WEARABLE_COVERAGE = 'at_wearable_coverage';
export const AT_TABLE_MAPPED_LAB_REPORT_RECORD = 'at_mapped_lab_report_record';
export const AT_TABLE_RECEIVED_LAB_REPORTS = 'at_received_lab_reports';
export const AT_TABLE_BATTERY_CHANGE_RECORDS = 'at_battery_change_records';
export const AT_TABLE_PERCEPTUAL_SURVEYS = 'at_perceptual_surveys';
export const AT_TABLE_MIGRATIONS = 'at_migrations';
export const AT_TABLE_PARTICIPANT_INTAKE_FORMS = 'at_participant_intake_forms';
export const AT_TABLE_HOUSE_INTAKE_FORMS = 'at_house_intake_forms';

// Views
export const AT_VIEW_PARTICIPANTS_PUBLIC = 'Public';

// The public participants export: which columns, and in what order.
//
// Both halves of the export need this. getParticipantsForExports filters the
// records down to it, and the browser passes it as the column order, which is
// not redundant: filterObjectByKeys leaves out a key the record does not have,
// so without it the header row would come from whichever record happened to be
// first.
export const ATF_PARTICIPANT_EXPORT_COLUMNS = [
  ATF_PUBLIC_ID,
  ATF_ACTIVE,
  ATF_AREA,
  ATF_TRADE,
  ATF_SEWA_ID,
  ATF_TYPE,
  ATF_NAME,
  ATF_LAST_NAME,
  ATF_PHONE_NUMBER,
  ATF_JOINED_DATE,
  ATF_AGE,
  ATF_ADDRESS,
  ATF_NOTES,
];

// Devices
export const DEVICE_SENSORS = 'Sensors';
export const DEVICE_PHONE = 'Location Tracker';
export const DEVICE_WEARABLE = 'Wearable';

// Device Status
export const DEVICE_STATUS_OK = 'DEVICE_STATUS_OK';
export const DEVICE_STATUS_WAITING_COLLECTION = 'DEVICE_STATUS_WAITING_COLLECTION';
export const DEVICE_STATUS_NEEDS_REFRESH = 'DEVICE_STATUS_NEEDS_REFRESH';
export const DEVICE_STATUS_WAITING_COLLECTION_LOOSING_DATA = 'DEVICE_STATUS_WAITING_COLLECTION_LOOSING_DATA';
export const DEVICE_STATUS_ERROR_IN_COLLECTION = 'DEVICE_STATUS_ERROR_IN_COLLECTION';
export const DEVICE_STATUS_HARDWARE_MALFUNCTION = 'DEVICE_STATUS_HARDWARE_MALFUNCTION';
export const DEVICE_STATUS_INCOMPLETE_DEPLOYMENT = 'DEVICE_STATUS_INCOMPLETE_DEPLOYMENT';
export const DEVICE_STATUS_OTHER = 'DEVICE_STATUS_OTHER';

// Messages
export const MESSAGE_DATA_UP_TO_DATE = 'messageDataUpToDate';
export const MESSAGE_PLACEMENT_JUST_STARTED = 'messagePlacementJustStarted';
export const MESSAGE_BATTERY_CHANGE_JUST_HAPPENED = 'messageBatteryChangeJustHappened';
export const MESSAGE_WAITING_FOR_COLLECTION = 'messageWaitingForCollection';
export const MESSAGE_WAITING_FOR_COLLECTION_LOOSING_DATA = 'messageWaitingForCollectionLoosingData';
export const MESSAGE_DATA_WAS_NOT_COLLECTED = 'messageDataWasNotCollected';
export const MESSAGE_TRACKER_SOFTWARE_NOT_UP_TO_DATE = 'messageTrackerSoftwareNotUpToDate';
export const MESSAGE_NO_FILES_HAVE_BEEN_RECEIVED = 'messageNoFilesHaveBeenReceived';
export const MESSAGE_INCORRECT_FREQUENCY = 'messageIncorrectFrequency';
export const MESSAGE_SENSOR_FILE_NOT_RECENT_ENOUGH = 'messageSensorFileNotRecentEnough';
export const MESSAGE_SENSOR_FILE_NOT_LONG_ENOUGH = 'messageSensorFileNotLongEnough';
export const MESSAGE_NO_SENSORS_IN_BUNDLE = 'messageNoSensorsInBundle';
export const MESSAGE_INCOMPLETE_DEPLOYMENT = 'messageIncompleteDeployment';
export const MESSAGE_ERROR_IN_COLLECTION = 'messageErrorInCollection';
export const MESSAGE_HANDLED_BY_CEPT = 'messageHandledByCEPT';
export const MESSAGE_MULTIPLE_INTAKES = 'messageMultipleIntakes';
export const MESSAGE_NO_INTAKES = 'messageNoIntakes';
export const MESSAGE_INTAKE_COMPLETE = 'messageIntakeComplete';
export const MESSAGE_INTAKE_WAITING = 'messageIntakeWaiting';
export const MESSAGE_PLEASE_REFRESH = 'messagePleaseRefresh';
export const MESSAGE_HAS_NEW_PENDING_ISSUES = 'messageHasNewPendingIssues';
export const MESSAGE_HAS_OVERLOOKED_ISSUES = 'messageHasOverlookedIssues';
export const MESSAGE_NO_ISSUES = 'messageNoIssues';
export const MESSAGE_HEART_RATE_NOT_RECENT = 'heartRateNotRecent';
export const MESSAGE_HAS_PENDING_QUESTIONS = 'messageHasPendingQuestions';
export const MESSAGE_HAS_PENDING_EXTRA_REQUESTS = 'messageHasPendingExtraRequests';
export const MESSAGE_NO_EXTRA_REQUESTS = 'messageNoExtraRequests';
export const MESSAGE_NETWORK_ERROR = 'messageNetworkError';
export const MESSAGE_INCORRECT_START_MINUTES = 'messageIncorrectStartMinutes';

export const MESSAGES_FOR_STATUS = {
  [MESSAGE_DATA_UP_TO_DATE]: 'Data up to date',
  [MESSAGE_PLACEMENT_JUST_STARTED]: 'Data up to date (Placement just started, no data expected yet)',
  [MESSAGE_BATTERY_CHANGE_JUST_HAPPENED]:
    'Data up to date (Batteries recently updated, no extensive data expected yet)',
  [MESSAGE_WAITING_FOR_COLLECTION]: 'Waiting for Collection',
  [MESSAGE_WAITING_FOR_COLLECTION_LOOSING_DATA]: 'Waiting for Collection (Loosing Data)',
  [MESSAGE_DATA_WAS_NOT_COLLECTED]: 'Data was not Collected',
  [MESSAGE_NO_FILES_HAVE_BEEN_RECEIVED]: 'No files have been received',
  [MESSAGE_SENSOR_FILE_NOT_RECENT_ENOUGH]:
    'Latest files not recent enough, please select a more recent end date for the export, or make sure the sensor has synced completely.',
  [MESSAGE_SENSOR_FILE_NOT_LONG_ENOUGH]: 'Latest files not long enough, please select an earlier start date',
  [MESSAGE_NO_SENSORS_IN_BUNDLE]: 'No sensors in bundle.',
  [MESSAGE_INCOMPLETE_DEPLOYMENT]: 'Incomplete Deployment',
  [MESSAGE_ERROR_IN_COLLECTION]: 'Error in collection',
  [MESSAGE_HANDLED_BY_CEPT]: 'Collection is Handled by the CEPT Team',
  [MESSAGE_MULTIPLE_INTAKES]: 'The participant has multiple active placements.',
  [MESSAGE_NO_INTAKES]: 'The participant has no active placements.',
  [MESSAGE_INTAKE_COMPLETE]: 'Intake Form up to date.',
  [MESSAGE_INTAKE_WAITING]: 'Please administer the intake form.',
  [MESSAGE_PLEASE_REFRESH]: 'Please refresh',
  [MESSAGE_HAS_NEW_PENDING_ISSUES]: 'Participant has new pending issues',
  [MESSAGE_NO_ISSUES]: 'No pending issues',
  [MESSAGE_HEART_RATE_NOT_RECENT]: 'Heart Rate not recent enough, please make sure heart rate synching.',
  [MESSAGE_HAS_PENDING_QUESTIONS]: 'Form has pending questions',
  [MESSAGE_HAS_PENDING_EXTRA_REQUESTS]: 'Participant has extra requests pending',
  [MESSAGE_NO_EXTRA_REQUESTS]: 'No extra requests pending',
  [MESSAGE_INCORRECT_FREQUENCY]: 'File received with an incorrect minute interval.',
  [MESSAGE_TRACKER_SOFTWARE_NOT_UP_TO_DATE]: 'The Location Tracker App is not up to date. Please Update it.',
  [MESSAGE_HAS_OVERLOOKED_ISSUES]: 'Participant has issues that have been overlooked.',
  [MESSAGE_NETWORK_ERROR]: 'Network error. Please check you have internet connection and try again.',
  [MESSAGE_INCORRECT_START_MINUTES]:
    'The start minutes of the sensor file is not correct. Make sure start minutes are multiples or 5 or set frequency to 1',
};

export const dashboardTranslation = (m) => (m in MESSAGES_FOR_STATUS ? MESSAGES_FOR_STATUS[m] : m);

// Tag Colors
export const TAG_COLORS = [
  'gold',
  'magenta',
  'blue',
  'lime',
  'volcano',
  'geekblue',
  'red',
  'green',
  'cyan',
  'orange',
  'purple',
];

// Airtable Object Sync Button Bheaviors
export const ATO_BEHAVIOR_PULL = 'pull';
export const ATO_BEHAVIOR_CREATE = 'create';
export const ATO_BEHAVIOR_UPDATE = 'update';
export const ATO_BEHAVIOR_UPDATE_CREATE = 'update_create';
export const ATO_BEHAVIOR_PULL_CREATE = 'pull_create';
export const ATO_BEHAVIOR_SYNC = 'sync';

export const COLORS = ['Green', 'Yellow', 'Red', 'Pink', 'Gray', 'Orange', 'White', 'Blue', 'Violet', 'Brown'];

export const SENSOR_POSITIONS = [
  'Both Sleep and Work',
  'Workplace',
  'Sleep Room',
  'Kitchen',
  'Field',
  ATF_VENDOR_SPOT,
  'Other',
];

export const NETWORK_ERROR = 'netrwork_error';
export const AIRTABLE_ERROR = 'airtable_error';
export const NOT_FOUND_ERROR = 'not_found_error';
export const UNOTHORIZED_ERROR = 'unothorized_error';
export const TOKEN_INVALID_ERROR = 'token_invalid_error';
export const CANT_REFRESH_TOKEN_ERROR = 'cant_refresh_token_error';
// No usable OAuth client for the account: the GCP Project column is unset, or it
// names a project the config file has no credentials for.
export const GOOGLE_PROJECT_NOT_ASSIGNED = 'google_project_not_assigned';

// Which API served a given account on a given run. The same two values the readings
// tables carry in their source_api column, so the two languages name a source alike
export const SOURCE_API_FITBIT = 'Fitbit';
export const SOURCE_API_GOOGLE = 'Google';

export const ERRORS = {
  [ERROR]: {
    [MESSAGE]: 'generalError',
    [CODE]: 1,
  },
  [NETWORK_ERROR]: {
    [MESSAGE]: 'networkError',
    [CODE]: 2,
  },
  [AIRTABLE_ERROR]: {
    [MESSAGE]: 'airtableError',
    [CODE]: 3,
  },
  [NOT_FOUND_ERROR]: {
    [MESSAGE]: 'notFoundError',
    [CODE]: 4,
  },
  [UNOTHORIZED_ERROR]: {
    [MESSAGE]: 'unothorizedError',
    [CODE]: 5,
  },
  [TOKEN_INVALID_ERROR]: {
    [MESSAGE]: 'tokenInvalidError',
    [CODE]: 6,
  },
  [CANT_REFRESH_TOKEN_ERROR]: {
    [MESSAGE]: 'cantRefreshTokenError',
    [CODE]: 6,
  },
};

// Likert
export const LIKERT_NUMERIC = 'numeric';
export const LIKERT_STRING = 'text';
export const DEFAULT_LIKERT = { [LIKERT_NUMERIC]: -1, [LIKERT_STRING]: NA };

// Surveys
export const SURVEY_IMAGES_FOLDER = 'survey_images';

// Perceptual Surveys
export const PERCEPTUAL_SURVEY_NAME = 'Perceptual Survey';
export const PERCEPTUAL_SURVEY = 'perceptual_survey';
export const PERCEPTUAL_SURVEY_FOLDER = 'perceptual_survey';
export const PERCEPTUAL_SURVEY_VERSION_FOLDER = 'perceptual_surveys';
export const PERCEPTUAL_SURVEY_VERSION_PREFIX = 'perceptual_survey_configuration-';
export const PERCEPTUAL_SURVEY_PREFIX = '__HF__';

export const PERCEPTUAL_WOMENS_HEALTH_SURVEY_NAME = 'Perceptual Womens Health Survey';
export const PERCEPTUAL_WOMENS_HEALTH_SURVEY = 'perceptual_womens_health_survey';
export const PERCEPTUAL_WOMENS_HEALTH_SURVEY_FOLDER = 'perceptual_womens_health_survey';
export const PERCEPTUAL_WOMENS_HEALTH_SURVEY_VERSION_FOLDER = 'perceptual_womens_health_surveys';
export const PERCEPTUAL_WOMENS_HEALTH_SURVEY_VERSION_PREFIX = 'perceptual_womens_health_survey_configuration-';
export const PERCEPTUAL_WOMENS_HEALTH_SURVEY_PREFIX = '__WHF__';
// Specific Questions
export const PERCEPTUAL_WOMENS_HEALTH_SURVEY_REPRODUCTIVE_STAGE = `reproductive_stage`;
export const PERCEPTUAL_WOMENS_HEALTH_SURVEY_CYCLE_REGULAR = `cycle_regular`;

export const PERCEPTUAL_WOMENS_HEALTH_SURVEY_REPRODUCTIVE_STAGE_PREGNANT = `Pregnant`;
export const PERCEPTUAL_WOMENS_HEALTH_SURVEY_REPRODUCTIVE_STAGE_LACTATING = `Lactating/Breastfeeding`;
export const PERCEPTUAL_WOMENS_HEALTH_SURVEY_REPRODUCTIVE_STAGE_MENSTRUATING = `Currently have a cycle/menstruating`;
export const PERCEPTUAL_WOMENS_HEALTH_SURVEY_REPRODUCTIVE_STAGE_NO_CYCLE = `No longer have a cycle/menstruating for a year or more`;

export const PERCEPTUAL_STREET_VENDOR_SURVEY_NAME = 'Perceptual Street Vendor Survey';
export const PERCEPTUAL_STREET_VENDOR_SURVEY = 'perceptual_street_vendor_survey';
export const PERCEPTUAL_STREET_VENDOR_SURVEY_FOLDER = 'perceptual_street_vendor_survey';
export const PERCEPTUAL_STREET_VENDOR_SURVEY_VERSION_FOLDER = 'perceptual_street_vendor_surveys';
export const PERCEPTUAL_STREET_VENDOR_SURVEY_VERSION_PREFIX = 'perceptual_street_vendor_survey_configuration-';
export const PERCEPTUAL_STREET_VENDOR_SURVEY_PREFIX = '__PSVF__';

export const PERCEPTUAL_MENTAL_HEALTH_SURVEY_NAME = 'Perceptual Mental Health Survey';
export const PERCEPTUAL_MENTAL_HEALTH_SURVEY = 'perceptual_mental_health_survey';
export const PERCEPTUAL_MENTAL_HEALTH_SURVEY_FOLDER = 'perceptual_mental_health_survey';
export const PERCEPTUAL_MENTAL_HEALTH_SURVEY_VERSION_FOLDER = 'perceptual_mental_health_surveys';
export const PERCEPTUAL_MENTAL_HEALTH_SURVEY_VERSION_PREFIX = 'perceptual_mental_health_survey_configuration-';
export const PERCEPTUAL_MENTAL_HEALTH_SURVEY_PREFIX = '__PMHF__';

// Intake Forms
export const PARTICIPANT_INTAKE_FORM_NAME = 'Participant Intake Form';
export const PARTICIPANT_INTAKE_FORM = 'participant_intake_form';
export const PARTICIPANT_INTAKE_FORM_FOLDER = 'participant_intake_form';
export const PARTICIPANT_INTAKE_FORM_VERSION_FOLDER = 'participant_intake_forms';
export const PARTICIPANT_INTAKE_FORM_VERSION_PREFIX = 'participant_intake_survey_configuration-';
export const PARTICIPANT_INTAKE_FORM_PREFIX = '__PIF__';

export const HOUSE_INTAKE_FORM_NAME = 'House Intake Form';
export const HOUSE_INTAKE_FORM = 'house_intake_form';
export const HOUSE_INTAKE_FORM_FOLDER = 'house_intake_form';
export const HOUSE_INTAKE_FORM_VERSION_FOLDER = 'house_intake_forms';
export const HOUSE_INTAKE_FORM_VERSION_PREFIX = 'house_intake_survey_configuration-';
export const HOUSE_INTAKE_FORM_PREFIX = '__HIF__';

export const PARTICIPANT_STREET_VENDOR_INTAKE_FORM_NAME = 'Participant Street Vendor Intake Form';
export const PARTICIPANT_STREET_VENDOR_INTAKE_FORM = 'participant_street_vendor_intake_form';
export const PARTICIPANT_STREET_VENDOR_INTAKE_FORM_FOLDER = 'participant_street_vendor_intake_form';
export const PARTICIPANT_STREET_VENDOR_INTAKE_FORM_VERSION_FOLDER = 'participant_street_vendor_intake_forms';
export const PARTICIPANT_STREET_VENDOR_INTAKE_FORM_VERSION_PREFIX =
  'participant_street_vendor_intake_survey_configuration-';
export const PARTICIPANT_STREET_VENDOR_INTAKE_FORM_PREFIX = '__PSVIF__';

// Configuration
export const SURVEY_STRUCTURE = 'structure';
export const SURVEY_SECTIONS = 'sections';
export const SURVEY_METADATA = 'metadata';
export const SURVEY_VERSION = 'version';
export const SURVEY_NAME = 'name';
export const SURVEY_FILTERED = 'filtered';

// Empty Survey Configuration
export const EMPTY_FORM_CONFIGURATION = {
  [SURVEY_METADATA]: {},
  [SURVEY_STRUCTURE]: [],
};

// Section Constants
export const SURVEY_SECTION_START = 'sectionStart';
export const SURVEY_SECTION_END = 'sectionEnd';

// Question Constants
export const SURVEY_QUESTION_ID = 'id';
export const SURVEY_QUESTION_SECTION = 'section';
export const SURVEY_QUESTION_TYPE = 'type';
export const SURVEY_QUESTION_TITLE = 'title';
export const SURVEY_QUESTION_INFO_TEXT = 'infoText';
export const SURVEY_QUESTION_TRANSLATION = 'translation';
export const SURVEY_QUESTION_IMAGE = 'image';
export const SURVEY_QUESTION_PRECISION = 'precision';
export const SURVEY_QUESTION_INCLUDE_NA = 'include_na';
export const SURVEY_QUESTION_INCLUDE_OTHER = 'include_other';
export const SURVEY_QUESTION_MIN_VALUE = 'min_value';
export const SURVEY_QUESTION_MAX_VALUE = 'max_value';
export const SURVEY_QUESTION_MIN_TEXT = 'min_text';
export const SURVEY_QUESTION_MAX_TEXT = 'max_text';
export const SURVEY_QUESTION_FOLLOWUP_TYPE = 'followup_type';
export const SURVEY_QUESTION_FOLLOWUP_PREFIX = 'followup_';
export const SURVEY_QUESTION_SHOW_FOLLOWUP_ON_ANSWER = 'show_followup_on_answer';
export const SURVEY_BINARY_FOLLOWUP_FIRST_QUESTION = 'first_question';
export const SURVEY_BINARY_FOLLOWUP_SECOND_QUESTION = 'second_question';

// Types
export const SURVEY_QUESTION_TYPE_MULTIPLE_CHOICE = 'multiple_choice';
export const SURVEY_QUESTION_TYPE_MULTIPLE_CHOICE_IMAGES = 'multiple_choice_images';
export const SURVEY_QUESTION_TYPE_MULTIPLE_CHOICE_AND_ANSWER = 'multiple_choice_answer';
export const SURVEY_QUESTION_TYPE_BINARY = 'binary';
export const SURVEY_QUESTION_TYPE_BINARY_FOLLOWUP = 'binary_followup';
export const SURVEY_QUESTION_TYPE_MULTIPLE_FOLLOWUP = 'multiple_followup';
export const SURVEY_QUESTION_TYPE_NUMERIC = 'numeric';
export const SURVEY_QUESTION_TYPE_CUSTOM = 'custom';
export const SURVEY_QUESTION_TYPE_IMAGE_UPLOAD = 'image_upload';
export const SURVEY_QUESTION_TYPE_TEXT_AREA = 'text_area';
export const SURVEY_QUESTION_TYPE_DATE = 'date';
export const SURVEY_QUESTION_TYPE_NUMERIC_SCALE = 'numeric_scale';

// Multiple Choice constants
export const SURVEY_MULTIPLE_CHOICE_OPTIONS = 'options';
export const SURVEY_MULTIPLE_CHOICE_IMAGES = 'images';
export const SURVEY_MULTIPLE_CHOICE_ANSWER_TYPE = 'answer_type';
export const SURVEY_MULTIPLE_CHOICE_INCLUDE_OTHER = 'include_other';
export const SURVEY_MULTIPLE_CHOICE_REMOVE_NONE = 'remove_none';
export const SURVEY_MULTIPLE_CHOICE_NONE_TEXT = 'none_text';

// Binary Constants
export const SURVEY_BINARY_OPTION_YES = 'yes';
export const SURVEY_BINARY_OPTION_NO = 'no';

// HealthForm Export constants
export const SURVEY_EXPORT_CONFIGURATION = 'configuration';
export const SURVEY_EXPORT_ANSWERS = 'answers';
export const SURVEY_EXPORT_PARTICIPANT_ID = 'id';
export const SURVEY_EXPORT_SEWA_ID = 'sewa_id';
export const SURVEY_EXPORT_PHONE_SERIAL = 'phone_serial';
export const SURVEY_EXPORT_DATETIME = 'datetime';
export const SURVEY_EXPORT_LOCALE = 'locale';
export const SURVEY_EXPORT_HOUSE_NUMBER = 'house_number';
export const SURVEY_EXPORT_HOUSE_ID = 'house_id';

export const QUESTION_ELEMENT = 'question_element';
// Custom types
// Blood Pressure
export const HF_CUSTOM_BLOOD_PRESSURE = 'blood_pressure';

export const HEALTH_FORM_SYSTOLIC_BLOOD_PRESSURE_1 = 'systolic_blood_pressure';
export const HEALTH_FORM_DIASTOLIC_BLOOD_PRESSURE_1 = 'diastolic_blood_pressure';
export const HEALTH_FORM_SYSTOLIC_BLOOD_PRESSURE_2 = 'systolic_blood_pressure_2';
export const HEALTH_FORM_DIASTOLIC_BLOOD_PRESSURE_2 = 'diastolic_blood_pressure_2';
export const HEALTH_FORM_SYSTOLIC_BLOOD_PRESSURE_3 = 'systolic_blood_pressure_3';
export const HEALTH_FORM_DIASTOLIC_BLOOD_PRESSURE_3 = 'diastolic_blood_pressure_3';

export const HF_BLOOD_PRESSURE_DEFAULT_ANSWER = {
  [HEALTH_FORM_SYSTOLIC_BLOOD_PRESSURE_1]: NA,
  [HEALTH_FORM_DIASTOLIC_BLOOD_PRESSURE_1]: NA,
  [HEALTH_FORM_DIASTOLIC_BLOOD_PRESSURE_1]: NA,
  [HEALTH_FORM_SYSTOLIC_BLOOD_PRESSURE_2]: NA,
  [HEALTH_FORM_DIASTOLIC_BLOOD_PRESSURE_2]: NA,
  [HEALTH_FORM_SYSTOLIC_BLOOD_PRESSURE_3]: NA,
  [HEALTH_FORM_DIASTOLIC_BLOOD_PRESSURE_3]: NA,
};

// Oral Temperature
export const HF_CUSTOM_ORAL_TEMPERATURE = 'oral_temperature';
export const HF_ORAL_TEMPERATURE_1 = 'oral_temperature';
export const HF_ORAL_TEMPERATURE_2 = 'oral_temperature_2';
export const HF_ORAL_TEMPERATURE_3 = 'oral_temperature_3';

export const HF_TEMPERATURE_DEFAULT_ANSWER = {
  [HF_ORAL_TEMPERATURE_1]: NA,
  [HF_ORAL_TEMPERATURE_2]: NA,
  [HF_ORAL_TEMPERATURE_3]: NA,
};

// Blood Glucose Level
export const HF_CUSTOM_BLOOD_GLUCOSE_LEVEL = 'blood_glucose_level';

// Reproductive cycle stage
export const PWHF_CUSTOM_REPRODUCTIVE_CYCLE_STAGE = 'reproductive_cycle_stage';
export const PWHF_CRCS_CYCLE_STAGE = 'cycle_stage';
export const PWHF_CRCS_DUE_DATE = 'due_date';

// Lab Results
export const LAB_RESULTS_FOLDER = 'lab_result';

export const REPORT_NAME_MASK = '000000000000000000000000';
export const REPORT_PHONE_MASK = '00000000000';

export const WEARABLE_COVERAGE_BACK_DAYS = 20;

// Different LABS
export const NEUBERG = 'Neuberg Supratech';
export const PATH_LABS = 'Dr. Lal Pathlabs';

// Specific Wiki Links
export const WIKI_SENSOR_PLACEMENT_PICTURE =
  'https://wiki.community-hats.net/en/process/place_sensor#taking-the-picture';
export const WIKI_LOCATION_TRACKER_APP =
  'https://wiki.community-hats.net/en/applications/community-hats-location-tracker-app';
